import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  acquireBottomImmersive,
  releaseBottomImmersive,
} from '../utils/flutterStatusBar'

/**
 * 코치마크(스포트라이트) 튜토리얼 오버레이.
 *
 * 실제 화면 요소 위에 어두운 막을 깔고, 대상 요소만 '구멍'을 뚫어 강조한 뒤
 * 그 옆에 설명 말풍선을 띄운다. 대상은 DOM의 [data-tutorial="키"] 로 찾는다
 * (컴포넌트 구조와 분리 — 각 화면은 대상 요소에 data-tutorial 속성만 붙이면 됨).
 *
 * 오버레이 아무 곳이나 탭하면 다음 스텝, 버튼으로 건너뛰기/완료.
 */
export interface CoachStep {
  /** 대상 요소의 data-tutorial 값. 없으면(요소 못 찾으면) 그 스텝은 건너뜀 */
  selector: string
  /** 제목(선택). 없으면 설명만 표시 */
  title?: string
  description: string
  /** 스포트라이트 여백(px). 기본 8. 하단 탭 아이콘 등은 작게. */
  pad?: number
  /** 스포트라이트 모서리 반경(px). 기본 14. 검색바/아이콘 등 대상에 맞춰. */
  radius?: number
  /** 이 스텝의 대상이 다른 페이지에 있으면, 그 페이지 경로. 스텝 진입 시 자동 이동한다.
   *  예: 마이 탭의 플레이트리스트 버튼 스텝 → '/main-my'. */
  navigateTo?: string
  /** 이 스텝의 버튼 문구(선택). 없으면 마지막='시작하기', 그 외='다음'. */
  buttonLabel?: string
}

interface Rect {
  top: number
  left: number
  width: number
  height: number
}

interface CoachMarkProps {
  steps: CoachStep[]
  onClose: () => void
}

const PAD = 8 // 스포트라이트 여백
const RADIUS = 14

const CoachMark: React.FC<CoachMarkProps> = ({ steps, onClose }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const [index, setIndex] = useState(0)
  const [rect, setRect] = useState<Rect | null>(null)

  const step = steps[index]
  const isLast = index >= steps.length - 1

  // 스텝의 대상이 다른 페이지에 있으면 그 페이지로 이동(진입 시). 이동 후 재측정 로직이 대상을 기다린다.
  useEffect(() => {
    if (step?.navigateTo && location.pathname !== step.navigateTo) {
      navigate(step.navigateTo)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index])

  // 네이티브 하단 스트립(홈 인디케이터 SafeArea)도 dim색으로 칠해 웹 오버레이와 이어지게.
  // (웹 dim은 뷰포트만 덮어서, 안 하면 하단 네이티브 스트립만 크림으로 남음)
  useEffect(() => {
    acquireBottomImmersive()
    return () => releaseBottomImmersive()
  }, [])

  // 튜토리얼 진행 중 배경 스크롤 잠금(스포트라이트 구멍 등으로 새는 스크롤까지 차단).
  // React onTouchMove는 passive라 preventDefault가 안 먹으므로 non-passive 네이티브 리스너로.
  useEffect(() => {
    const prevent = (e: TouchEvent) => e.preventDefault()
    document.addEventListener('touchmove', prevent, { passive: false })
    return () => document.removeEventListener('touchmove', prevent)
  }, [])

  const findRect = useCallback((): Rect | null => {
    if (!step) return null
    const el = document.querySelector<HTMLElement>(
      `[data-tutorial="${step.selector}"]`
    )
    if (!el) return null
    const r = el.getBoundingClientRect()
    return { top: r.top, left: r.left, width: r.width, height: r.height }
  }, [step])

  // 스텝이 바뀌면 대상 측정. 대상이 아직 없으면 잠깐 후 재시도하고,
  // 여러 번(≈0.8s) 실패해야 그 스텝을 건너뛴다(마지막이면 닫기).
  // 즉시 스킵하면 마운트/레이아웃 타이밍에 스텝이 연쇄로 넘어가 자동 종료되는 사고 방지.
  useLayoutEffect(() => {
    let tries = 0
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | undefined
    const attempt = () => {
      if (cancelled) return
      const r = findRect()
      if (r !== null) {
        setRect(r)
        return
      }
      if (tries++ < 8) {
        timer = setTimeout(attempt, 100)
        return
      }
      if (isLast) onClose()
      else setIndex((i) => i + 1)
    }
    attempt()
    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
  }, [findRect, isLast, onClose])

  // 리사이즈/스크롤 시 위치 재측정
  useEffect(() => {
    const onReflow = () => {
      const r = findRect()
      if (r) setRect(r)
    }
    window.addEventListener('resize', onReflow)
    window.addEventListener('scroll', onReflow, true)
    return () => {
      window.removeEventListener('resize', onReflow)
      window.removeEventListener('scroll', onReflow, true)
    }
  }, [findRect])

  const next = useCallback(() => {
    if (isLast) onClose()
    else setIndex((i) => i + 1)
  }, [isLast, onClose])

  if (!step || !rect) return null

  const pad = step.pad ?? PAD
  const radius = step.radius ?? RADIUS
  const holeTop = rect.top - pad
  const holeLeft = rect.left - pad
  const holeW = rect.width + pad * 2
  const holeH = rect.height + pad * 2

  // 대상이 화면 아래쪽(하단바 등)이면 말풍선을 위에, 아니면 아래에 둔다
  const viewportH = window.innerHeight
  const below = holeTop + holeH < viewportH * 0.6
  const tooltipTop = below ? holeTop + holeH + 14 : undefined
  const tooltipBottom = below ? undefined : viewportH - holeTop + 14

  return createPortal(
    <div className="fixed inset-0 z-[2000]" onClick={next} role="presentation">
      {/* 스포트라이트: box-shadow로 바깥 전체를 어둡게, 안쪽만 투명 */}
      <div
        style={{
          position: 'absolute',
          top: holeTop,
          left: holeLeft,
          width: holeW,
          height: holeH,
          borderRadius: radius,
          boxShadow: '0 0 0 9999px rgba(0,0,0,0.4)',
          // 다음 스텝으로 넘어갈 때 네모가 슬라이드('휘릭' 이동)하지 않고 새 위치에 바로 나타나도록 트랜지션 제거
          pointerEvents: 'none',
        }}
      />

      {/* 설명 말풍선 */}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'absolute',
          top: tooltipTop,
          bottom: tooltipBottom,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 'calc(100% - 48px)',
          maxWidth: 340,
        }}
        className="rounded-16px bg-white px-20px py-18px shadow-lg"
      >
        {step.title && (
          <h3 className="font-pretendard font-bold text-17px leading-150% text-brown-80%-main">
            {step.title}
          </h3>
        )}
        <p
          className={`${
            step.title ? 'mt-6px' : ''
          } font-pretendard font-regular text-15px leading-150% text-brown-80%-main`}
        >
          {step.description}
        </p>

        <button
          type="button"
          onClick={next}
          className="mt-16px w-full rounded-12px bg-brandPrimary py-12px font-pretendard font-bold text-15px text-white"
        >
          {step.buttonLabel ?? (isLast ? '시작하기' : '다음')}
        </button>
      </div>
    </div>,
    document.body
  )
}

export default CoachMark
